import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useTheme } from "../context/ThemeContext";
import logviComplete from "../assets/icons/logvi-complete.png";

/** Resolver de avatar para CRA/Webpack */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

const API_BASE = "https://api.logvi-havox.com";

type CommandItem = {
  codigo: string;
  rota: string;
  aceitaArgumento?: boolean;
  buildRoute?: (argumento: string) => string;
};

const COMMANDS: CommandItem[] = [
  { codigo: "HOME", rota: "/acess/home" },

  {
    codigo: "SMIGO02",
    rota: "/suprimentos/estoque-p-servico-e-comercial/estoque",
  },

  { codigo: "LLAB01", rota: "/acess/laboratorio/cases/novo" },
  { codigo: "LLAB02", rota: "/acess/laboratorio/cases" },
  {
    codigo: "LLAB03",
    rota: "/acess/laboratorio/cases",
    aceitaArgumento: true,
    buildRoute: (argumento: string) =>
      argumento
        ? `/acess/laboratorio/cases/${encodeURIComponent(argumento)}`
        : "/acess/laboratorio/cases",
  },
  { codigo: "LEST01", rota: "/acess/estoque/atual" },
  { codigo: "LEST02", rota: "/acess/estoque/movimentacoes" },
  { codigo: "LPROD01", rota: "/acess/estoque/produtos" },
  { codigo: "LCOMP01", rota: "/acess/compras/solicitacoes/novo" },
  { codigo: "LCOMP02", rota: "/acess/compras/solicitacoes" },
  { codigo: "LCOMP03", rota: "/acess/compras/aprovacoes" },
  { codigo: "LFIN01", rota: "/acess/financeiro/contas-a-pagar" },
  { codigo: "LFIN02", rota: "/acess/financeiro/contas-a-receber" },
  { codigo: "LCLI01", rota: "/acess/clientes" },
  { codigo: "LFOR01", rota: "/acess/fornecedores" },
  { codigo: "LDOC01", rota: "/acess/documentos" },
  { codigo: "LAUD01", rota: "/acess/auditoria/eventos" },
  { codigo: "LGOV01", rota: "/acess/governanca/classificacoes" },
  { codigo: "LUSER01", rota: "/acess/usuarios" },
];

function hashIndex(seed: string): number {
  let h = 0;

  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }

  return h % 6;
}

function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];

    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1;

  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];

  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  return undefined;
}

function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : "";

  return (first + second).toUpperCase();
}

function getDisplayName(fullName?: string | null) {
  const clean = String(fullName || "").trim();

  if (!clean) return "Usuário";

  const parts = clean.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  return parts[0];
}

function formatCnpj(cnpj?: string | null) {
  const digits = String(cnpj || "").replace(/\D/g, "");

  if (digits.length !== 14) return cnpj || "";

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(
    5,
    8
  )}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

function parseCommandInput(value: string) {
  const clean = value.trim().replace(/\s+/g, " ");
  const [rawCode, ...rest] = clean.split(" ");

  return {
    clean,
    code: String(rawCode || "").toUpperCase(),
    argument: rest.join(" ").trim(),
  };
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { sessao, logout } = useAuth();
  const { systemTitle } = useTheme();

  const menuRef = useRef<HTMLDivElement | null>(null);
  const commandInputRef = useRef<HTMLInputElement | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [userAreaHovered, setUserAreaHovered] = useState(false);
  const [commandValue, setCommandValue] = useState("");
  const [commandFocused, setCommandFocused] = useState(false);
  const [commandError, setCommandError] = useState(false);

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "Departamento não identificado";
  const empresaRazaoSocial = sessao?.empresa_razao_social || "ERRO INTERNO";
  const empresaCnpj = sessao?.empresa_cnpj || "";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const displayName = useMemo(() => getDisplayName(nome), [nome]);

  const formattedCnpj = useMemo(() => formatCnpj(empresaCnpj), [empresaCnpj]);

  const profileUrl = useMemo(
    () => resolveProfileUrl(pictureId, nome),
    [pictureId, nome]
  );

  const hasName = !!nome;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;

      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      const isCommandShortcut =
        (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k";

      if (isCommandShortcut) {
        e.preventDefault();
        setMenuOpen(false);

        requestAnimationFrame(() => {
          commandInputRef.current?.focus();
          commandInputRef.current?.select();
        });

        return;
      }

      if (e.key === "Escape") {
        setMenuOpen(false);
        commandInputRef.current?.blur();
      }
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const executeCommand = () => {
    const parsed = parseCommandInput(commandValue);

    if (!parsed.clean) return;

    const command = COMMANDS.find((item) => item.codigo === parsed.code);

    if (!command) {
      setCommandError(true);

      window.setTimeout(() => {
        setCommandError(false);
      }, 900);

      return;
    }

    const route =
      command.aceitaArgumento && command.buildRoute
        ? command.buildRoute(parsed.argument)
        : command.rota;

    setCommandValue("");
    setCommandError(false);
    commandInputRef.current?.blur();

    navigate(route, { replace: false });
  };

  const onCommandKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeCommand();
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      commandInputRef.current?.blur();
    }
  };

  const doLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    setMenuOpen(false);

    try {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch {}

      try {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("github_oauth_state");
      } catch {}

      await logout();

      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <motion.header
      style={styles.container}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={styles.leftBlock}
      >
        <button
          type="button"
          style={styles.systemInfo}
          onClick={() => navigate("/acess/home")}
        >
          <img
            src={logviComplete}
            alt="Logvi"
            style={styles.systemLogo}
            draggable={false}
          />

          <span style={styles.separator}>|</span>

          <span style={styles.systemTitle}>{systemTitle || "Sistema"}</span>

          <span style={styles.separator}>|</span>

          <span style={styles.companyInfo}>
            {empresaRazaoSocial}
            {formattedCnpj ? ` ${formattedCnpj}` : ""}
          </span>
        </button>

        <span style={styles.separatorOutside}>|</span>

        <div
          style={{
            ...styles.commandShell,
            ...(commandFocused ? styles.commandShellFocused : {}),
            ...(commandError ? styles.commandShellError : {}),
          }}
        >
          <span style={styles.commandLabel}>TX</span>

          <input
            ref={commandInputRef}
            value={commandValue}
            onChange={(e) => {
              setCommandValue(e.target.value.toUpperCase());
              if (commandError) setCommandError(false);
            }}
            onFocus={() => {
              setCommandFocused(true);
              setMenuOpen(false);
            }}
            onBlur={() => setCommandFocused(false)}
            onKeyDown={onCommandKeyDown}
            placeholder="Comando"
            autoComplete="off"
            spellCheck={false}
            style={styles.commandInput}
            title="Ex.: HOME, LEST01, LLAB03 148"
          />

          <button
            type="button"
            style={styles.commandSubmit}
            onClick={executeCommand}
            aria-label="Executar comando"
            title="Executar comando"
          >
            <svg width="12" height="12" viewBox="0 0 24 24">
              <path
                d="M5 12h13M13 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </motion.div>

      <div style={styles.userInfo} ref={menuRef}>
        <motion.button
          type="button"
          style={{
            ...styles.userTrigger,
            ...(userAreaHovered ? styles.userTriggerHover : {}),
            ...(menuOpen ? styles.userTriggerOpen : {}),
          }}
          onMouseEnter={() => setUserAreaHovered(true)}
          onMouseLeave={() => setUserAreaHovered(false)}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          whileHover={{ y: -0.5 }}
          whileTap={{ scale: 0.985 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
        >
          <span style={styles.avatarRing}>
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={displayName}
                style={styles.avatar}
                draggable={false}
                loading="lazy"
              />
            ) : (
              <span style={{ ...styles.avatar, ...styles.avatarFallback }}>
                {hasName ? getInitials(nome) : ""}
              </span>
            )}
          </span>

          <span style={styles.name}>{displayName}</span>

          <span
            style={{
              ...styles.chevron,
              transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
            aria-hidden="true"
          >
            <svg width="13" height="13" viewBox="0 0 24 24">
              <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </motion.button>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <div style={styles.accountDetails}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Empresa</span>
                  <span style={styles.detailValue}>{empresaRazaoSocial}</span>
                </div>

                {formattedCnpj && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>CNPJ</span>
                    <span style={styles.detailValue}>{formattedCnpj}</span>
                  </div>
                )}

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Depto.</span>
                  <span style={styles.detailValue}>{departamento}</span>
                </div>
              </div>

              <div style={styles.menuActions}>
                <button
                  type="button"
                  style={styles.menuActionItem}
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/acess/home", { replace: false });
                  }}
                >
                  <span style={styles.actionIcon} aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24">
                      <path
                        d="M4 11.5L12 5l8 6.5V20a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1v-8.5z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>

                  <span>
                    <strong style={styles.actionTitle}>Página inicial</strong>
                    <small style={styles.actionSubtitle}>
                      Retornar ao painel principal
                    </small>
                  </span>
                </button>
              </div>

              <div style={styles.logoutArea}>
                <button
                  type="button"
                  style={{
                    ...styles.logoutButton,
                    ...(loggingOut ? styles.logoutButtonDisabled : {}),
                  }}
                  onClick={doLogout}
                  disabled={loggingOut}
                >
                  <span style={styles.logoutIcon} aria-hidden="true">
                    {loggingOut ? (
                      <span style={styles.spinner} />
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24">
                        <path
                          d="M10 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M14 16l4-4-4-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 12h10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </span>

                  <span>
                    {loggingOut ? "Encerrando sessão..." : "Encerrar sessão"}
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "52px",
    minHeight: "52px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(160px, auto)",
    alignItems: "center",
    gap: 18,
    padding: "0 16px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 5000,
    boxSizing: "border-box",
    background: "linear-gradient(270deg, #094350 1%, #062128 99%)",
    border: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
    overflow: "visible",
  },

  leftBlock: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    height: "100%",
    gap: 10,
  },

  systemInfo: {
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: 9,
    minWidth: 0,
    height: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    cursor: "pointer",
    userSelect: "none",
  },

  systemLogo: {
    height: 17,
    width: "auto",
    objectFit: "contain",
    flexShrink: 0,
    display: "block",
    pointerEvents: "none",
  },

  systemTitle: {
    fontSize: 12.5,
    fontWeight: 800,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    flexShrink: 0,
    lineHeight: 1,
  },

  companyInfo: {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    lineHeight: 1,
  },

  separator: {
    opacity: 0.55,
    color: "#FFFFFF",
    flexShrink: 0,
    lineHeight: 1,
  },

  separatorOutside: {
    opacity: 0.45,
    color: "#FFFFFF",
    flexShrink: 0,
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 700,
  },

  commandShell: {
    width: 202,
    minWidth: 202,
    height: 23,
    minHeight: 23,
    display: "flex",
    alignItems: "center",
    borderRadius: 4,
    border: "none",
    background: "rgba(255,255,255,0.07)",
    overflow: "hidden",
    boxSizing: "border-box",
    transition:
      "background 0.16s ease, box-shadow 0.16s ease, outline-color 0.16s ease",
    outline: "1px solid rgba(255,255,255,0.07)",
    outlineOffset: 0,
    flexShrink: 0,
  },

  commandShellFocused: {
    background: "rgba(255,255,255,0.12)",
    outline: "1px solid rgba(255,255,255,0.20)",
    boxShadow: "0 0 0 2px rgba(255,255,255,0.035)",
  },

  commandShellError: {
    background: "rgba(239,68,68,0.13)",
    outline: "1px solid rgba(239,68,68,0.75)",
    boxShadow: "0 0 0 2px rgba(239,68,68,0.12)",
  },

  commandLabel: {
    height: "100%",
    minWidth: 27,
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.12)",
    color: "rgba(255,255,255,0.68)",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 9.3,
    fontWeight: 900,
    letterSpacing: "0.055em",
    lineHeight: 1,
    boxSizing: "border-box",
  },

  commandInput: {
    flex: 1,
    height: 23,
    minHeight: 23,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.8,
    fontWeight: 800,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    padding: "0 6px",
    lineHeight: "23px",
    boxSizing: "border-box",
  },

  commandSubmit: {
    height: 23,
    width: 25,
    minWidth: 25,
    border: "none",
    borderLeft: "1px solid rgba(255,255,255,0.075)",
    background: "transparent",
    color: "rgba(255,255,255,0.70)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    lineHeight: 1,
  },

  userInfo: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    position: "relative",
    minWidth: 0,
    zIndex: 5100,
  },

  userTrigger: {
    height: 32,
    maxWidth: 220,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "0 4px 0 0",
    borderRadius: 6,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    color: "#FFFFFF",
    boxSizing: "border-box",
    transition: "background 0.16s ease, transform 0.16s ease",
  },

  userTriggerHover: {
    background: "rgba(255,255,255,0.055)",
  },

  userTriggerOpen: {
    background: "rgba(255,255,255,0.075)",
  },

  avatarRing: {
    width: 28,
    height: 28,
    minWidth: 28,
    minHeight: 28,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
    boxShadow: "none",
  },

  avatar: {
    width: 28,
    height: 28,
    minWidth: 28,
    minHeight: 28,
    borderRadius: "50%",
    objectFit: "cover",
    userSelect: "none",
    background: "#0B5268",
    display: "block",
  },

  avatarFallback: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  name: {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12.2,
    fontWeight: 850,
    lineHeight: 1,
    whiteSpace: "nowrap",
    maxWidth: 136,
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#FFFFFF",
  },

  chevron: {
    width: 14,
    height: 14,
    minWidth: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.18s ease",
    opacity: 0.78,
    color: "#FFFFFF",
  },

  menu: {
    position: "absolute",
    top: "calc(100% + 9px)",
    right: 0,
    width: 318,
    borderRadius: 8,
    background: "rgba(255,255,255,0.982)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow:
      "0 24px 70px rgba(0,0,0,0.22), 0 1px 0 rgba(255,255,255,0.86) inset",
    overflow: "hidden",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },

  accountDetails: {
    padding: "12px 16px",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#FFFFFF",
  },

  detailRow: {
    display: "grid",
    gridTemplateColumns: "70px 1fr",
    gap: 10,
    alignItems: "start",
  },

  detailLabel: {
    color: "#94A3B8",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: 1.35,
  },

  detailValue: {
    color: "#334155",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.35,
    textTransform: "uppercase",
    overflowWrap: "anywhere",
  },

  menuActions: {
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    background: "#FFFFFF",
  },

  menuActionItem: {
    width: "100%",
    minHeight: 50,
    padding: "9px 10px",
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "#17313A",
    textAlign: "left",
    fontFamily: '"Nunito", "Inter", sans-serif',
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    transition: "background 0.16s ease, transform 0.16s ease",
  },

  actionIcon: {
    width: 31,
    height: 31,
    minWidth: 31,
    borderRadius: 8,
    background: "rgba(14,50,58,0.07)",
    color: "#0E323A",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  actionTitle: {
    display: "block",
    color: "#0F172A",
    fontSize: 12.5,
    fontWeight: 900,
    lineHeight: 1.2,
  },

  actionSubtitle: {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.25,
    marginTop: 2,
  },

  logoutArea: {
    padding: "10px",
    background: "rgba(248,250,252,0.96)",
    borderTop: "1px solid rgba(15,23,42,0.06)",
  },

  logoutButton: {
    width: "100%",
    minHeight: 43,
    borderRadius: 8,
    border: "1px solid rgba(185,28,28,0.13)",
    background: "rgba(254,242,242,0.82)",
    color: "#991B1B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition:
      "background 0.16s ease, border-color 0.16s ease, transform 0.16s ease, opacity 0.16s ease",
  },

  logoutButtonDisabled: {
    opacity: 0.72,
    cursor: "not-allowed",
  },

  logoutIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  spinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid rgba(153,27,27,0.25)",
    borderTopColor: "#991B1B",
    animation: "spin 0.8s linear infinite",
  },
};

export default Header;
